import { ReactNode, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Scale, History, FlaskConical, BarChart3, ScrollText, HeartPulse, ShieldAlert, ChevronDown, TrendingUp } from 'lucide-react';
import { ConnectionStatus } from './ConnectionStatus';
import { HealthStatusButton } from './HealthStatusButton';
import { AppVersionButton } from './AppVersionButton';
import { ProductionLinkButton } from './ProductionLinkButton';
import { UserProfile } from './UserProfile';
import { useSignalR } from '../contexts/useSignalR';
import { useViewMode } from '../hooks/useViewMode';

interface AppLayoutProps {
  children: ReactNode;
  pageTitle?: string;
  pageDescription?: string;
}

export const AppLayout = ({ children }: AppLayoutProps) => {
  const { connectionState } = useSignalR();
  const navigate = useNavigate();
  const location = useLocation();
  const [isLocationMenuOpen, setIsLocationMenuOpen] = useState(false);
  const [hoverTimeout, setHoverTimeout] = useState<number | null>(null);
  const { viewMode } = useViewMode();

  const getCurrentPageInfo = () => {
    if (location.pathname.startsWith('/opinions/prompts')) {
      return { 
        title: 'Prompts', 
        icon: <ScrollText className="w-5 h-5" />,
        path: '/opinions/prompts'
      };
    }
    if (location.pathname.startsWith('/opinions/evaluation')) {
      return { 
        title: 'Evaluation', 
        icon: <FlaskConical className="w-5 h-5" />,
        path: '/opinions/evaluation'
      };
    }
    if (location.pathname.startsWith('/opinions/scoring')) {
      return { 
        title: 'Scoring', 
        icon: <BarChart3 className="w-5 h-5" />,
        path: '/opinions/scoring'
      };
    }
    if (location.pathname.startsWith('/opinions/history')) {
      return { 
        title: 'History', 
        icon: <History className="w-5 h-5" />,
        path: '/opinions/history'
      };
    }
    if (location.pathname.startsWith('/opinions/model-health')) {
      return { 
        title: 'Model Health', 
        icon: <HeartPulse className="w-5 h-5" />,
        path: '/opinions/model-health'
      };
    }
    if (location.pathname.startsWith('/opinions/hallucination')) {
      return { 
        title: 'Hallucination', 
        icon: <ShieldAlert className="w-5 h-5" />,
        path: '/opinions/hallucination'
      };
    }
    if (location.pathname.startsWith('/civil-lawsuits/trends/pre-computed')) {
      return { 
        title: 'Pre-computed Trends', 
        icon: <TrendingUp className="w-5 h-5" />,
        path: '/civil-lawsuits/trends/pre-computed'
      };
    }
    if (location.pathname.startsWith('/civil-lawsuits/trends/queried')) {
      return { 
        title: 'Trends', 
        icon: <TrendingUp className="w-5 h-5" />,
        path: '/civil-lawsuits/trends/queried'
      };
    }
    return { 
      title: 'Search', 
      icon: <Scale className="w-5 h-5" />,
      path: '/opinions/search'
    };
  };

  const currentPage = getCurrentPageInfo();

  const pageGroups = [
    {
      category: 'OPINIONS',
      items: [
        { title: 'Search', icon: <Scale className="w-5 h-5" />, path: '/opinions/search' },
        { title: 'History', icon: <History className="w-5 h-5" />, path: '/opinions/history' },
        { title: 'Evaluation', icon: <FlaskConical className="w-5 h-5" />, path: '/opinions/evaluation' },
        { title: 'Scoring', icon: <BarChart3 className="w-5 h-5" />, path: '/opinions/scoring' },
        { title: 'Prompts', icon: <ScrollText className="w-5 h-5" />, path: '/opinions/prompts' },
        { title: 'Model Health', icon: <HeartPulse className="w-5 h-5" />, path: '/opinions/model-health' },
        { title: 'Hallucination', icon: <ShieldAlert className="w-5 h-5" />, path: '/opinions/hallucination' }
      ]
    },
    {
      category: 'CIVIL LAWSUITS',
      items: [
        { title: 'Trends', icon: <TrendingUp className="w-5 h-5" />, path: '/civil-lawsuits/trends/queried' },
        { title: 'Pre-computed Trends', icon: <TrendingUp className="w-5 h-5" />, path: '/civil-lawsuits/trends/pre-computed' }
      ]
    }
  ];

  const handlePageSelect = (path: string) => {
    navigate(path);
    setIsLocationMenuOpen(false);
  };

  const handleMouseEnter = () => {
    if (hoverTimeout) {
      clearTimeout(hoverTimeout);
    }
    const timeout = setTimeout(() => {
      setIsLocationMenuOpen(true);
    }, 200);
    setHoverTimeout(timeout);
  };

  const handleMouseLeave = () => {
    if (hoverTimeout) {
      clearTimeout(hoverTimeout);
      setHoverTimeout(null);
    }
    setIsLocationMenuOpen(false);
  };

  return (
    <div data-component="AppLayout" className="flex flex-col h-screen bg-chat-bg">
      {/* Main Application Header - Hidden in user view mode */}
      {viewMode !== 'user' && (
        <header className="bg-white border-b border-gray-200 px-6 py-4 flex-shrink-0">
          <div className="max-w-7xl mx-auto flex items-center justify-between">
            {/* Left: Location Switcher */}
            <div 
              className="relative"
              onMouseEnter={handleMouseEnter}
              onMouseLeave={handleMouseLeave}
            >
              <button
                onClick={() => setIsLocationMenuOpen(!isLocationMenuOpen)}
                className="flex items-center gap-3 px-4 py-2 text-gray-900 bg-gray-50 hover:bg-gray-100 rounded-lg transition-colors border border-gray-200"
                data-flow={isLocationMenuOpen ? 'open' : 'closed'}
              >
                {currentPage.icon}
                <span className="font-semibold text-base">{currentPage.title}</span>
                <ChevronDown className={`w-4 h-4 text-gray-500 transition-transform ${isLocationMenuOpen ? 'rotate-180' : ''}`} />
              </button>

              {/* Dropdown Menu */}
              {isLocationMenuOpen && (
                <>
                  <div 
                    className="fixed inset-0 z-10" 
                    onClick={() => setIsLocationMenuOpen(false)}
                  />
                  <div 
                    data-flow="location-menu"
                    className="absolute top-full left-0 mt-2 w-56 bg-white rounded-lg shadow-lg border border-gray-200 py-2 z-20"
                  >
                    {pageGroups.map((group) => (
                      <div key={group.category}>
                        <div className="px-4 py-2 text-xs font-semibold text-gray-400 uppercase tracking-wider">
                          {group.category}
                        </div>
                        {group.items.map((page) => (
                          <a
                            key={page.path}
                            href={page.path}
                            onClick={(e) => {
                              if (e.button === 0 && !e.ctrlKey && !e.metaKey) {
                                e.preventDefault();
                                handlePageSelect(page.path);
                              }
                            }}
                            onAuxClick={(e) => {
                              if (e.button === 1) {
                                e.preventDefault();
                              }
                            }}
                            className={`w-full flex items-center gap-3 px-4 py-2.5 text-left transition-colors no-underline ${
                              page.path === currentPage.path
                                ? 'bg-blue-50 text-blue-700'
                                : 'text-gray-700 hover:bg-gray-50'
                            }`}
                            data-flow={page.path === currentPage.path ? 'active' : 'inactive'}
                          >
                            {page.icon}
                            <span className="font-medium">{page.title}</span>
                          </a>
                        ))}
                      </div>
                    ))}
                  </div>
                </>
              )}
            </div>

            {/* Right: Environment Version, Health, Connection, User */}
            <div className="flex items-center gap-3">
              <ProductionLinkButton />
              <AppVersionButton />
              <HealthStatusButton />
              <ConnectionStatus connectionState={connectionState} />
              <UserProfile />
            </div>
          </div>
        </header>
      )}

      {/* Content Area */}
      <div className="flex-1 overflow-hidden flex flex-col">
        {children}
      </div>
    </div>
  );
};
